import React, { useState } from 'react';
import { FilePlus, X } from 'lucide-react';
import { apiService } from '../../../../components/api/apiService';
import LoaderOverlay from '../../Dialogs/LoaderOverlay';

interface Props {
  onClose: () => void;
  tenantId: string;
  knowledgeBaseId: string; 
  kbTitle: string;
}

const AddDocumentsToKbModal: React.FC<Props> = ({
  onClose,
  tenantId,
  knowledgeBaseId, 
  kbTitle,
}) => {
  const [namespace, setNamespace] = useState('');
  const [documents, setDocuments] = useState<{ fileName: string; filePath: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const uploaded: { fileName: string; filePath: string }[] = [];
    setUploading(true);
    setLoaderMessage('Uploading files...');
    for (const file of files) {
      const formData = new FormData();
      formData.append('tenantId', tenantId);
      formData.append('file', file);
      try {
        const res = await apiService.postForm('api/Upload/file', formData, 'backend');
        uploaded.push({ fileName: file.name, filePath: res.fullPath });
      } catch (err) {
        console.error('Upload failed for', file.name, err);
      }
    }
    setDocuments((prev) => [...prev, ...uploaded]);
    setUploading(false);
    setLoaderMessage('');
  };

  const handleSubmit = async () => {
    if (!namespace || documents.length === 0) {
      alert('Please provide a namespace and select at least one document.');
      return;
    }

    setLoading(true);
    setLoaderMessage('Saving documents...');

    try {
      const payload = {
        knowledgeBaseId,
        tenantId,
        namespace,
        documents,
      };

      await apiService.post('api/knowledgebase/add-documents', payload, 'backend');
      alert('Documents uploaded successfully!');
      onClose();
    } catch (error) {
      console.error('Submit failed:', error);
      alert('Upload failed. Try again.');
    } finally {
      setLoading(false);
      setLoaderMessage('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 text-sm">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-neutral-900 rounded-xl border border-neutral-800 text-white shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <h2 className="flex items-center gap-2 font-semibold">
            <FilePlus className="w-5 h-5 text-blue-400" />
            Upload to: <span className="text-white">{kbTitle}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <label className="block text-gray-400 mb-1">Namespace</label>
            <input
              placeholder="e.g., finance_docs"
              className="w-full bg-neutral-800 px-3 py-2 rounded border border-neutral-700 outline-none focus:ring-1 focus:ring-indigo-500"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-gray-400 mb-1">Upload Files</label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="text-white bg-neutral-800 file:bg-neutral-700 file:text-white file:px-3 file:py-1 file:border-0 rounded cursor-pointer"
            />
            {uploading && <p className="mt-1 text-yellow-400">Uploading...</p>}
            {!uploading && documents.length > 0 && (
              <div className="mt-2 text-green-400 space-y-1">
                <p>{documents.length} file(s) uploaded:</p>
                <ul className="list-disc list-inside text-green-300">
                  {documents.map((doc, index) => (
                    <li key={index}>{doc.fileName}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-800 bg-neutral-950">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md text-sm shadow"
          >
            Submit
          </button>
        </div>
      </div>

      {(uploading || loading) && <LoaderOverlay message={loaderMessage} />}
    </div>
  );
};

export default AddDocumentsToKbModal;
